// Shared presentation formatters for the web app.
//
// One home for the GBP currency / compact-abbreviation / British date helpers
// that were copy-pasted across pages and components. British English + en-GB
// locale throughout (project convention). These are unsigned helpers matching
// the historical onboarding/ops pages; the sign-aware KPI variants live in
// `components/kpi/format.ts` (flows and performance go negative there).

/** Full GBP with thousands separators, no decimals — `£1,234,567`. */
export function formatCurrencyGBP(value: number): string {
    return `£${value.toLocaleString('en-GB')}`;
}

/**
 * Sign-aware, multi-currency amount with 2dp — `-£1,234.50` / `$99.00` /
 * `12.00 EUR`. Renders GBP/USD symbols; any other currency falls back to a
 * trailing ISO code. Shared by the Ops + Desk overdrawn-cash surfaces (the Desk
 * page is a read-only mirror of the Ops one, so the two always drift together).
 */
export function formatSignedCurrency(value: number, currency: string): string {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '';
    const formatted = abs.toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    if (symbol) return `${sign}${symbol}${formatted}`;
    return `${sign}${formatted} ${currency}`;
}

/**
 * Compact GBP abbreviation — `£2.30B` / `£1.5M` / `£250K` / `£500`.
 * Unsigned (matches the onboarding/ops pipeline semantics).
 */
export function formatCompactGBP(value: number): string {
    if (value >= 1_000_000_000) return `£${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `£${Math.round(value / 1_000)}K`;
    return `£${value.toLocaleString('en-GB')}`;
}

/**
 * British short date — `02 Jun 2026` (en-GB, `dd MMM yyyy`). Passes a malformed
 * or unparseable input back through unchanged. Date-only strings (`YYYY-MM-DD`)
 * are anchored to UTC midnight so they don't drift a day in negative timezones.
 */
export function formatDateGB(iso: string): string {
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00.000Z` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * British month + year — `March 2026` (en-GB, `MMMM yyyy`). Accepts a full ISO
 * timestamp or a `YYYY-MM` / `YYYY-MM-DD` month key (anchored to UTC midnight so
 * it never drifts a month in negative timezones). Unparseable input passes back
 * through unchanged.
 */
export function formatMonthYearGB(iso: string): string {
    const normalised = iso.length === 7 ? `${iso}-01` : iso;
    const d = new Date(normalised.length <= 10 ? `${normalised}T00:00:00.000Z` : normalised);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Machine key → sentence case for display: `dateOfBirth` → "Date of birth",
 * `no_health_concerns` → "No health concerns", `addressLine1` → "Address line 1".
 *
 * This is the FALLBACK for keys with no curated label, not a substitute for one —
 * a hand-written label always reads better and survives a key rename. Reach for a
 * `Record<key, string>` map first (see `contact-labels.ts`) and let this catch
 * whatever the map does not name, so a new key degrades to readable prose rather
 * than leaking camelCase into the UI.
 */
export function humaniseKey(key: string): string {
    const spaced = key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
    if (spaced.length === 0) return key;
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
